<template>
    <div class="ChangeID">
        <PageTitle>{{ $t('personal-account.changeID.title') }}</PageTitle>
        <h3>{{ $t('personal-account.changeID.description') }}</h3>
        <div class="ChangeID-blockContainer">
            <div class="row">
                <div class="col col-12 col-lg-6">
                    <Block>
                        <div
                            class="ChangeID-documentTypeBlock"
                            :class="{'ChangeID-documentTypeBlock--active': cardOpen === 'passport'}"
                            @click="setCardOpen('passport')"
                            @keyDown.enter="setCardOpen('passport')"
                        >
                            <span class="ChangeID-cardIcon f2m-passport" />
                            <span class="ChangeID-cardTitle">{{ $t('personal-account.changeID.passport.label') }}</span>
                            <div
                                class="ChangeID-cardUploadContainer"
                                :class="{'ChangeID-cardUploadContainer--active': cardOpen === 'passport'}"
                            >
                                <span class="ChangeID-cardUploadLabel">{{ $t('personal-account.changeID.passport.uploadLabel') }}</span>
                                <FileInput
                                    :id="'passport'"
                                    v-model="passport.value"
                                    :errorLabel="getError('passport', passport.errors)"
                                    :captureLabel="$t('personal-account.changeID.document.label-capture-picture')"
                                    :label="$t('personal-account.changeID.document.recto')"
                                    class="ChangeID-uploadInput"
                                    data-cy="input.passport"
                                />
                                <RoundedButton
                                    :label="$t('personal-account.changeID.passport.uploadButton')"
                                    class="ChangeID-button"
                                    :inactive="passport.value === ''"
                                    type="reversed"
                                    data-cy="button.upload"
                                    @buttonClicked="submitPassport([passport.value])"
                                />
                            </div>
                        </div>
                    </Block>
                </div>
                <div class="col col-12 col-lg-6">
                    <Block>
                        <div
                            class="ChangeID-documentTypeBlock"
                            :class="{'ChangeID-documentTypeBlock--active': cardOpen === 'id-card'}"
                            @click="setCardOpen('id-card')"
                            @keyDown.enter="setCardOpen('id-card')"
                        >
                            <span class="ChangeID-cardIcon f2m-id-card" />
                            <span class="ChangeID-cardTitle">{{ $t('personal-account.changeID.id-card.label') }}</span>
                            <div
                                class="ChangeID-cardUploadContainer"
                                :class="{'ChangeID-cardUploadContainer--active': cardOpen === 'id-card'}"
                            >
                                <span class="ChangeID-cardUploadLabel">{{ $t('personal-account.changeID.id-card.uploadLabel') }}</span>
                                <FileInput
                                    :id="'id-card-recto'"
                                    v-model="idCardRecto.value"
                                    :errorLabel="getError('idCardRecto', idCardRecto.errors)"
                                    :captureLabel="$t('personal-account.changeID.document.label-capture-picture')"
                                    :label="$t('personal-account.changeID.document.recto')"
                                    class="ChangeID-uploadInput"
                                    data-cy="input.id-card-recto"
                                />
                                <FileInput
                                    :id="'id-card-verso'"
                                    v-model="idCardVerso.value"
                                    :errorLabel="getError('idCardVerso', idCardVerso.errors)"
                                    :captureLabel="$t('personal-account.changeID.document.label-capture-picture')"
                                    :label="$t('personal-account.changeID.document.verso')"
                                    class="ChangeID-uploadInput"
                                    data-cy="input.id-card-verso"
                                />
                                <RoundedButton
                                    :label="$t('personal-account.changeID.id-card.uploadButton')"
                                    class="ChangeID-button"
                                    :inactive="idCardRecto.value === '' || idCardVerso.value === ''"
                                    type="reversed"
                                    data-cy="button.upload"
                                    @buttonClicked="submitIdCard([idCardRecto.value, idCardVerso.value])"
                                />
                            </div>
                        </div>
                    </Block>
                </div>
            </div>
        </div>
        <Transition
            name="fade"
            mode="out-in"
        >
            <FullScreenModal
                :showModal="showSuccessModal"
                type="success"
                :contentStyle="{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    height: '100%',
                    paddingBottom: '120px',
                    overflowX: 'hidden',
                    overflowY: 'auto'
                }"
                :contentSlotStyle="{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexGrow: '1',
                    margin: '0 20px',
                }"
            >
                <div class="SuccessModal">
                    <div class="SuccessModal-icon">
                        <span class="f2m-check" />
                    </div>
                    <div
                        class="SuccessModal-message"
                        v-html="$t(`personal-account.changeID.success.message`)"
                    />
                </div>
            </FullScreenModal>
        </Transition>
    </div>
</template>

<script>
import PageTitle from "@/components/PageTitle.vue";
import Transition from "@/components/Transition.vue";
import FullScreenModal from "@/components/FullScreenModal.vue";

import {Block, RoundedButton} from '@free2move/vuejs-components';
import FileInput from "@/components/FileInput.vue";
import {required} from "@free2move/vuejs-components/validators.js";
import {SEND_SUPPORTING_DOCUMENT} from "@/store/action-types";
import Component from "vue-class-component";
import Vue from "vue";

@Component({
    components: {
        PageTitle,
        Block,
        FileInput,
        RoundedButton,
        Transition,
        FullScreenModal,
    },
})
export default class ChangeID extends Vue {
    data() {
        return {
            cardOpen: '',
            passport: {
                value: '',
                validators: [required],
                errors: [],
            },
            idCardRecto: {
                value: '',
                validators: [required],
                errors: [],
            },
            idCardVerso: {
                value: '',
                validators: [required],
                errors: [],
            },
            showSuccessModal: false,
        };
    }

    getError(name, errors) {
        return errors.length > 0 ? this.$t(`personal-account.changeID.${name}.${errors[0]}`) : '';
    }

    submitPassport() {
        this.$store.dispatch(SEND_SUPPORTING_DOCUMENT, {
            data: {
                type: 'passport',
                files: [this.passport.value],
            },
            callback: ({error}) => {
                if (!error) {
                    this.showSuccessModal = true;
                    setTimeout(() => {
                        this.showSuccessModal = false
                        this.$router.push({name: 'personalAccountProfile'});
                    }, 3000);
                }
            },
        })
    }

    submitIdCard() {
        this.$store.dispatch(SEND_SUPPORTING_DOCUMENT, {
            data: {
                type: 'id-card',
                files: [this.idCardRecto.value, this.idCardVerso.value],
            },
            callback: ({error}) => {
                if (!error) {
                    this.showSuccessModal = true;
                    setTimeout(() => {
                        this.showSuccessModal = false
                        this.$router.push({name: 'personalAccountProfile'});
                    }, 3000);
                }
            },
        })
    }

    setCardOpen(card) {
        this.cardOpen = card;
    }
}
</script>

<style lang="scss" scoped>
.ChangeID {
    h3 {
        font-family: $font-lato;
        font-style: normal;
        font-weight: 400;
        font-size: 22px;
        line-height: 26px;
        color: #FFFFFF;
        mix-blend-mode: normal;
        opacity: 0.7;
    }

    &-blockContainer {
        margin-top: 60px;

        .col{
            margin-bottom: 30px;
        }
    }

    &-documentTypeBlock{
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        cursor: pointer;
        transition:all 1s ease;

        &--active{
            cursor: initial;
        }
    }

    &-cardIcon {
        font-size: 55px;
        color: $color-f2m-pink;
    }

    &-cardTitle{
        font-weight: 800;
        font-size: 18px;
        line-height: 22px;
    }

    &-cardUploadContainer{
        width: 100%;
        margin-top: 0px;
        height: 0;
        overflow:hidden;
        padding: 0 2px;
        position: relative;
        transition: all 0.5s ease;
        -webkit-animation: slideUp 0.5s;

        &--active{
            height: auto;
            margin-top: 28px;
            -webkit-animation: slideDown 0.3s;
        }
    }

    &-uploadInput{
        width: 100%;

        @media (min-width: $f2m-desktop-breakpoint) {
            width: 250px;
        }
    }
}

.SuccessModal{
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: $color-white;
    font-size: 30px;
    line-height: 36px;
    text-align: center;

    &-icon{
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 119px;
        min-width: 119px;
        border: 2px solid $color-white;
        border-radius: 50%;
        margin-bottom: 21px;

        span{
            font-size: 50px;
        }
    }

    &-verifiedEmail {
        max-width: 200px;
    }

    &-resetPassword {
        max-width: 500px;
        text-align: left;
    }
}

@keyframes slideDown {
    from {
        transform: translateY(-100%);
        opacity: 0;
    }
    to {
        transform: translateY(0);
        opacity: 1;
    }
}
@keyframes slideUp {
    from {
        transform: translateY(0);
        opacity: 1;
    }
    to {
        transform: translateY(-100%);
        opacity: 0;
    }
}
</style>
